import React, { useState, useEffect, useMemo } from 'react'
import { Row, Col, message } from 'antd'
import { SearchOutlined, InfoCircleFilled } from '@ant-design/icons'
import studentRequest from 'utils/studentRequest'
import ActiveAssessmentFilter from './ActiveAssessmentFilter'
import CompletedFilter from './CompletedAssessmentFilter'
import DropoutAssessmentFilter from './DropoutAssessmentFilter'
import AssessmentTable from './AssessmentTable'
import DiagnosisSection from './DiagnosisSection'
import InstituteToggle from '../../../../components/Toggle/InstituteToggle'
import { 
  generatePDFReport, 
  checkStudentCorporateStatus 
} from '../../actions'
import {
  AssessmentCard,
  AssessmentHeader,
  SearchSection,
  SearchInput,
  ShowingText,
  NoteSection,
  TabsContainer,
  Tab,
  ContentContainer,
  ShowingTextContainer,
  EntityToggleWrapper,
  FilterBadgeBar,
  FilterBadge,
  BadgeCount,
  MoreBadgesButton,
} from '../../Style'

import { useDispatch, useSelector } from 'react-redux'
import { trackAssessmentEntered } from '../../../../utils/assessmentEvents'

const AssessmentContainer = ({
  setAssessment,
  activeAssessments,
  activeAssessmentsLoading,
  activeAssessmentsFilters,
  completedAssessments,
  completedAssessmentsLoading,
  completedAssessmentsFilters,
  fetchActiveAssessments,
  fetchCompletedAssessments,
  setActiveAssessmentFilters,
  setCompletedAssessmentFilters,
  setCurrentAssessmentId,
  studentId,
  dropoutAssessments,
  dropoutAssessmentsLoading,
  dropoutAssessmentsFilters,
  fetchDropoutAssessments,
  setDropoutAssessmentFilters,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('active')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [activeBadges, setActiveBadges] = useState([])
  const [showAllSpecBadges, setShowAllSpecBadges] = useState(false)
  const [activeEntityTab, setActiveEntityTab] = useState('institute')
  const [completedAssessmentsFetched, setCompletedAssessmentsFetched] = useState(false)

  const dispatch = useDispatch()

  const { instituteCount, corporateCount } = useMemo(() => {
    if (!activeAssessments) return { instituteCount: 0, corporateCount: 0 }
    const institute = activeAssessments.filter(a => !a.isCorporate).length
    const corporate = activeAssessments.filter(a => a.isCorporate).length
    return { instituteCount: institute, corporateCount: corporate }
  }, [activeAssessments])
  
  // Get user data - properly extract email from nested structure
  const user = useSelector(state => {
    console.log('State auth user:', state.auth?.user);
    console.log('State studentDetails:', state.studentData?.studentDetails);
    const userData = state.auth?.user || state.studentData?.studentDetails;
    console.log('Current user data:', userData);
    return userData;
  })
  
  // Extract email correctly from the nested structure
  const userEmail = user?.studentPersonalProfile?.primaryEmail ||
                    user?.email || 
                    user?.login_email || 
                    user?.student?.email ||
                    user?.admin?.login_email;

  // For debugging
  useEffect(() => {
    console.log('User object updated:', user);
    console.log('Extracted email:', userEmail);
  }, [user, userEmail]);



  // Get corporate status from the API response
  const isStudentCorporateOnly = useSelector(state => state.assessment?.studentCorporateStatus?.isCorporate || false)
  const corporateStatusLoading = useSelector(state => state.assessment?.studentCorporateStatus?.loading || false)

  // Debug corporate status
  useEffect(() => {
    console.log('Current corporate status:', isStudentCorporateOnly);
    console.log('Corporate status loading:', corporateStatusLoading);
  }, [isStudentCorporateOnly, corporateStatusLoading])

  // Check if the student is corporate-only when component mounts or when userEmail becomes available
  useEffect(() => {
    console.log('Checking if should call API, userEmail:', userEmail);
    if (userEmail) {
      console.log('Dispatching checkStudentCorporateStatus for:', userEmail);
      dispatch(checkStudentCorporateStatus(userEmail));
    } else {
      console.log('No email available yet, skipping API call');
      
      // If we have a user ID but no email, try to use that
      if (user?.id) {
        console.log('Trying to use student ID for API call:', user.id);
        // You might need to adjust your API action to accept ID instead of email if needed
      }
    }
  }, [userEmail, user?.id, dispatch])

  // Update activeEntityTab when corporate status changes
  useEffect(() => {
    if (isStudentCorporateOnly) {
      setActiveEntityTab('corporate')
    }
  }, [isStudentCorporateOnly])

  // Fetch active assessments and diagnosis status when component mounts
  useEffect(() => {
    if (studentId) {
      fetchActiveAssessments(studentId, {
        duration: activeAssessmentsFilters?.duration || [],
        assessmentType: activeAssessmentsFilters?.assessmentType || [],
        search: searchQuery,
      })

    }
  }, [activeAssessmentsFilters, searchQuery, fetchActiveAssessments, studentId, dispatch])

  // Fetch completed assessments when component mounts (needed for diagnosis logic)
  useEffect(() => {
    if (studentId && !completedAssessmentsFetched) {
      fetchCompletedAssessments(studentId, {
        timeFilter: completedAssessmentsFilters?.timeFilter || [],
        assessmentType: completedAssessmentsFilters?.assessmentType || [],
        search: '',
      })
      setCompletedAssessmentsFetched(true)
    }
  }, [studentId, fetchCompletedAssessments, completedAssessmentsFetched, completedAssessmentsFilters])

  // Re-fetch completed assessments when filters change and user is on completed tab
  useEffect(() => {
    if (studentId && activeTab === 'completed' && completedAssessmentsFetched) {
      fetchCompletedAssessments(studentId, {
        timeFilter: completedAssessmentsFilters?.timeFilter || [],
        assessmentType: completedAssessmentsFilters?.assessmentType || [],
        search: searchQuery,
      })
    }
  }, [completedAssessmentsFilters, searchQuery, fetchCompletedAssessments, studentId, activeTab, completedAssessmentsFetched])

  // Fetch dropout assessments only when dropout tab is active
  useEffect(() => {
    if (studentId && activeTab === 'dropout') {
      fetchDropoutAssessments(studentId, {
        timeFilter: dropoutAssessmentsFilters?.timeFilter || [],
        assessmentType: dropoutAssessmentsFilters?.assessmentType || [],
        search: searchQuery,
      })
    }
  }, [dropoutAssessmentsFilters, searchQuery, fetchDropoutAssessments, studentId, activeTab])

  const handleDeadlineChange = value => {
    setActiveAssessmentFilters({
      ...activeAssessmentsFilters,
      duration: value,
    })
  }

  const handleAssessmentTypeChange = checkedValues => {
    if (activeTab === 'active') {
      setActiveAssessmentFilters({
        ...activeAssessmentsFilters,
        assessmentType: checkedValues,
      })
    } else if (activeTab === 'completed') {
      setCompletedAssessmentFilters({
        ...completedAssessmentsFilters,
        assessmentType: checkedValues,
      })
    } else if (activeTab === 'dropout') {
      setDropoutAssessmentFilters({
        ...dropoutAssessmentsFilters,
        assessmentType: checkedValues,
      })
    }
  }

  const handleTimeFilterChange = value => {
    if (activeTab === 'completed') {
      setCompletedAssessmentFilters({
        ...completedAssessmentsFilters,
        timeFilter: value,
      })
    } else if (activeTab === 'dropout') {
      setDropoutAssessmentFilters({
        ...dropoutAssessmentsFilters,
        timeFilter: value,
      })
    }
  }

  const handleClearAll = () => {
    if (activeTab === 'active') {
      setActiveAssessmentFilters({
        ...activeAssessmentsFilters,
        duration: [''],
        assessmentType: [],
      })
    } else if (activeTab === 'completed') {
      setCompletedAssessmentFilters({
        ...completedAssessmentsFilters,
        timeFilter: [''],
        assessmentType: [],
      })
    } else if (activeTab === 'dropout') {
      setDropoutAssessmentFilters({
        ...dropoutAssessmentsFilters,
        timeFilter: [''],
        assessmentType: [],
      })
    }
  }

  const handleSearch = e => {
    const value = e.target.value
    setSearchKeyword(value)
    setSearchQuery(value)
  }

  const handleTabClick = (tab) => {
    setActiveTab(tab)
    setActiveBadges([])
  }

  const handleTakeAssessment = async (assessment) => {
    // Broadcast: lazy bind via POST /broadcast/:id/start, then proceed as normal
    if (assessment.rowSource === 'broadcast' && assessment.broadcastId && !assessment.assessment_assigned_id) {
      try {
        message.loading({ content: 'Preparing your assessment...', key: 'broadcast-start' })
        const res = await studentRequest.post(
          `/assessment/broadcast/${assessment.broadcastId}/start`,
          { studentId }
        )
        const data = res?.data?.data
        if (data?.assessmentAssignedId) {
          message.success({ content: 'Assessment ready!', key: 'broadcast-start', duration: 2 })
          const boundAssessment = {
            ...assessment,
            assessment_assigned_id: data.assessmentAssignedId,
            assessment_set_id: data.assessmentSetId,
            id: assessment.id,
            rowSource: 'direct',
          }
          setAssessment(boundAssessment)
          setCurrentAssessmentId(boundAssessment)
          trackAssessmentEntered(data.assessmentAssignedId, assessment.type, assessment.isCorporate, assessment.entityName)
          return
        }
      } catch (err) {
        console.error('Broadcast start error:', err)
        message.error({ content: err.response?.data?.message || 'Failed to start broadcast assessment', key: 'broadcast-start' })
        return
      }
    }

    setAssessment(assessment)
    setCurrentAssessmentId(assessment.id)
    trackAssessmentEntered(assessment.assessment_assigned_id, assessment.type, assessment.isCorporate, assessment.entityName)
  }

  // Returns the promise so ViewReportButton can guard against rage-clicks.
  // The catch keeps the previous behaviour (log, no crash) and still settles the
  // promise, so the guard releases the button once the download finishes.
  const handleViewReport = (assessmentId) =>
    dispatch(generatePDFReport(assessmentId)).catch(error => {
      console.error('Error generating PDF:', error)
    })

  const handleEntityToggle = (entityType) => {
    setActiveEntityTab(entityType)
  }

  const filterAssessments = (assessments, query) => {
    if (!query) return assessments
    return assessments?.filter(assessment =>
      assessment.title?.toLowerCase().includes(query.toLowerCase()) ||
      assessment.name?.toLowerCase().includes(query.toLowerCase()) ||
      assessment.type?.toLowerCase().includes(query.toLowerCase())
    )
  }

  // Filter assessments based on entity type for any tab
  const getFilteredAssessmentsByEntity = (assessments) => {
    if (!assessments) return []
    
    const filteredBySearch = filterAssessments(assessments, searchQuery)
    
    // If student is corporate-only, only show corporate assessments regardless of tab
    if (isStudentCorporateOnly) {
      return filteredBySearch?.filter(assessment => assessment.isCorporate)
    }
    
    // Normal filtering based on selected tab
    if (activeEntityTab === 'institute') {
      return filteredBySearch?.filter(assessment => !assessment.isCorporate)
    } else {
      return filteredBySearch?.filter(assessment => assessment.isCorporate)
    }
  }

  // Filter active assessments based on entity type
  const getFilteredActiveAssessments = () => {
    return getFilteredAssessmentsByEntity(activeAssessments)
  }

  // Filter completed assessments based on entity type
  const getFilteredCompletedAssessments = () => {
    return getFilteredAssessmentsByEntity(completedAssessments)
  }

  // Filter dropout assessments based on entity type
  const getFilteredDropoutAssessments = () => {
    return getFilteredAssessmentsByEntity(dropoutAssessments)
  }

  // The diagnosis a student still has to sit, read straight off the ACTIVE
  // list — a diagnosis that has been submitted has already left it.
  //
  // This used to be counted instead: "2 minus every completed assessment of
  // this type". That counted unrelated completed papers as diagnosis, which
  // shrank the list below to `slice(0, left)` and dropped a diagnosis the
  // student still owed — and since the regular table hides diagnosis rows,
  // there was then nowhere left to take it from.
  const getPendingDiagnosis = () => {
    if (activeEntityTab !== 'institute') {
      return { communication: [], aptitude: [] }
    }
    const active = getFilteredActiveAssessments() || []
    const pending = type =>
      active.filter(
        a => a.type === type && a.is_diagnosis === true && a.is_one_time !== true
      )
    return {
      communication: pending('Communication'),
      aptitude: pending('Aptitude'),
    }
  }

  const getDiagnosisAssessments = () => {
    const pending = getPendingDiagnosis()
    return {
      communication: {
        type: 'Communication',
        assessments: pending.communication,
        needsDiagnosis: pending.communication.length > 0,
      },
      aptitude: {
        type: 'Aptitude',
        assessments: pending.aptitude,
        needsDiagnosis: pending.aptitude.length > 0,
      },
    }
  }

  // Regular assessments of a type stay locked while a diagnosis of that type is
  // still outstanding. "Nothing outstanding" unlocks — which also covers the
  // student who was never assigned a diagnosis at all, who the completed-count
  // version would have locked out forever.
  const getIndividualDiagnosisStatus = () => {
    const pending = getPendingDiagnosis()
    return {
      communication: pending.communication.length === 0,
      aptitude: pending.aptitude.length === 0,
    }
  }

  const isDiagnosisCompleted = () => {
    const status = getIndividualDiagnosisStatus()
    return status.communication && status.aptitude
  }

  // Get remaining assessments (after diagnosis ones)
  const getRemainingAssessments = () => {
    const filteredAssessments = getFilteredActiveAssessments()
    if (!filteredAssessments) return []

    // For Corporate tab, return all assessments since there's no diagnosis
    if (activeEntityTab !== 'institute') {
      return filteredAssessments
    }

    const diagnosisData = getDiagnosisAssessments()
    const diagnosisIds = [
      ...diagnosisData.communication.assessments.map(a => a.id),
      ...diagnosisData.aptitude.assessments.map(a => a.id)
    ]

    return filteredAssessments.filter(assessment => !diagnosisIds.includes(assessment.id))
  }

  // Check if user has institute assessments for any tab
  const hasInstituteAssessments = activeAssessments?.some(assessment => !assessment.isCorporate) ||
                                 completedAssessments?.some(assessment => !assessment.isCorporate) ||
                                 dropoutAssessments?.some(assessment => !assessment.isCorporate)
  
  // Check if user has corporate assessments for any tab
  const hasCorporateAssessments = activeAssessments?.some(assessment => assessment.isCorporate) ||
                                 completedAssessments?.some(assessment => assessment.isCorporate) ||
                                 dropoutAssessments?.some(assessment => assessment.isCorporate)

  // Set default tab based on available assessments
  React.useEffect(() => {
    if (activeAssessments?.length > 0) {
      if (hasInstituteAssessments) {
        setActiveEntityTab('institute')
      } else if (hasCorporateAssessments) {
        setActiveEntityTab('corporate')
      }
    }
  }, [activeAssessments, hasInstituteAssessments, hasCorporateAssessments])

  // Get the source list for badge computation based on current tab
  const getBadgeSourceList = () => {
    if (activeTab === 'completed') return getFilteredCompletedAssessments() || []
    if (activeTab === 'dropout') return getFilteredDropoutAssessments() || []
    return getRemainingAssessments() || []
  }

  // Badge filter data — recomputes when tab or entity changes
  const badgeData = useMemo(() => {
    const all = getBadgeSourceList()
    const direct = all.filter(a => a.rowSource !== 'broadcast')
    const communication = all.filter(
      a => a.type?.toLowerCase() === 'communication'
    )
    const aptitude = all.filter(
      a => a.type?.toLowerCase() === 'behavior' || a.type?.toLowerCase() === 'aptitude'
    )
    const studentSpecName = all.find(
      a => a.rowSource === 'broadcast' && a.broadcastMatchesSpec && a.studentSpecName
    )?.studentSpecName
    const matchingSpec = all.filter(a => a.rowSource === 'broadcast' && a.broadcastMatchesSpec)
    const otherSpecs = {}
    all.filter(a => a.rowSource === 'broadcast' && !a.broadcastMatchesSpec).forEach(a => {
      if (a.labelSpecialisations) {
        a.labelSpecialisations.forEach(spec => {
          if (!otherSpecs[spec]) otherSpecs[spec] = []
          otherSpecs[spec].push(a)
        })
      }
    })
    const deptScopes = {}
    all.filter(a => a.rowSource === 'broadcast').forEach(a => {
      if (a.broadcastScope) {
        a.broadcastScope.forEach(scope => {
          if (!deptScopes[scope]) deptScopes[scope] = []
          deptScopes[scope].push(a)
        })
      }
    })
    return { direct, communication, aptitude, studentSpecName, matchingSpec, otherSpecs, deptScopes }
  }, [activeAssessments, completedAssessments, dropoutAssessments, activeEntityTab, activeTab])

  const matchesBadge = (assessment, badge) => {
    switch (badge) {
      case 'assigned': return assessment.rowSource !== 'broadcast'
      case 'communication': return assessment.type?.toLowerCase() === 'communication'
      case 'aptitude': return assessment.type?.toLowerCase() === 'behavior' || assessment.type?.toLowerCase() === 'aptitude'
      case 'my-spec': return assessment.rowSource === 'broadcast' && assessment.broadcastMatchesSpec
      default:
        if (badge.startsWith('spec:')) return assessment.rowSource === 'broadcast' && !assessment.broadcastMatchesSpec && assessment.labelSpecialisations?.includes(badge.replace('spec:', ''))
        if (badge.startsWith('dept:')) return assessment.rowSource === 'broadcast' && assessment.broadcastScope?.includes(badge.replace('dept:', ''))
        return false
    }
  }

  const getBadgeFilteredAssessments = (assessments) => {
    if (!activeBadges || activeBadges.length === 0) return assessments
    return assessments.filter(a => activeBadges.some(badge => matchesBadge(a, badge)))
  }

  const handleBadgeClick = badge => setActiveBadges(prev =>
    prev.includes(badge) ? prev.filter(b => b !== badge) : [...prev, badge]
  )

  const otherSpecNames = Object.keys(badgeData.otherSpecs)
  const visibleOtherSpecs = showAllSpecBadges ? otherSpecNames : otherSpecNames.slice(0, 2)
  const hiddenSpecCount = otherSpecNames.length - 2

  const diagnosisData = getDiagnosisAssessments()
  const remainingAssessments = getBadgeFilteredAssessments(getRemainingAssessments())
  const diagnosisCompleted = isDiagnosisCompleted()
  const individualDiagnosisStatus = getIndividualDiagnosisStatus()
  const filteredActiveAssessments = getFilteredActiveAssessments()
  const filteredCompletedAssessments = getBadgeFilteredAssessments(getFilteredCompletedAssessments())
  const filteredDropoutAssessments = getBadgeFilteredAssessments(getFilteredDropoutAssessments())

  // Check if any diagnosis sections are showing - ONLY for Institute tab
  const hasDiagnosisSections = (
    activeEntityTab === 'institute' && (
      (diagnosisData.communication.needsDiagnosis && diagnosisData.communication.assessments.length > 0) ||
      (diagnosisData.aptitude.needsDiagnosis && diagnosisData.aptitude.assessments.length > 0)
    )
  )

  const totalItems =
    activeTab === 'active'
      ? filteredActiveAssessments?.length || 0
      : activeTab === 'completed'
      ? filteredCompletedAssessments?.length || 0
      : activeTab === 'dropout'
      ? filteredDropoutAssessments?.length || 0
      : 0

  return (
    <>
      <Row gutter={40}>
        {activeTab !== 'reports' && (
          <Col xs={24} sm={24} md={6} lg={6}>
            {activeTab === 'active' ? (
              <ActiveAssessmentFilter
                deadlineFilter={activeAssessmentsFilters?.duration || []}
                assessmentTypeFilter={
                  activeAssessmentsFilters?.assessmentType || []
                }
                onDeadlineChange={handleDeadlineChange}
                onAssessmentTypeChange={handleAssessmentTypeChange}
                onClearAll={handleClearAll}
              />
            ) : activeTab === 'completed' ? (
              <CompletedFilter
                timeFilter={completedAssessmentsFilters?.timeFilter || []}
                assessmentTypeFilter={
                  completedAssessmentsFilters?.assessmentType || []
                }
                onTimeFilterChange={handleTimeFilterChange}
                onAssessmentTypeChange={handleAssessmentTypeChange}
                onClearAll={handleClearAll}
              />
            ) : activeTab === 'dropout' ? (
              <DropoutAssessmentFilter
                timeFilter={dropoutAssessmentsFilters?.timeFilter || []}
                assessmentTypeFilter={
                  dropoutAssessmentsFilters?.assessmentType || []
                }
                onTimeFilterChange={handleTimeFilterChange}
                onAssessmentTypeChange={handleAssessmentTypeChange}
                onClearAll={handleClearAll}
              />
            ) : null}
          </Col>
        )}
        <Col
          xs={24}
          sm={24}
          md={activeTab === 'reports' ? 24 : 18}
          lg={activeTab === 'reports' ? 24 : 18}
        >
          <AssessmentCard>
            {/* Entity Toggle - Only show if NOT corporate-only*/}
            {activeTab !== 'reports' && !isStudentCorporateOnly && (
              <EntityToggleWrapper>
                <InstituteToggle
                  value={activeEntityTab}
                  onChange={handleEntityToggle}
                  hasInstitute={true}
                  hasCorporate={true}
                  instituteCount={instituteCount}
                  corporateCount={corporateCount}
                  disabled={false}
                />
              </EntityToggleWrapper>
            )}

            <TabsContainer>
              <Tab
                active={activeTab === 'active'}
                onClick={() => handleTabClick('active')}
              >
                Active Assessments
              </Tab>
              <Tab
                active={activeTab === 'completed'}
                onClick={() => handleTabClick('completed')}
              >
                Completed Assessments
              </Tab>
              <Tab
                active={activeTab === 'dropout'}
                onClick={() => handleTabClick('dropout')}
              >
                Dropout Assessments
              </Tab>
              <Tab
                active={activeTab === 'reports'}
                onClick={() => handleTabClick('reports')}
              >
                Reports
              </Tab>
            </TabsContainer>

            {activeTab !== 'reports' && (
              <FilterBadgeBar>
                {badgeData.direct.length > 0 && (
                  <FilterBadge active={activeBadges.includes('assigned')} onClick={() => handleBadgeClick('assigned')}>
                    Assigned to you <BadgeCount active={activeBadges.includes('assigned')}>{badgeData.direct.length}</BadgeCount>
                  </FilterBadge>
                )}
                {badgeData.communication.length > 0 && (
                  <FilterBadge active={activeBadges.includes('communication')} onClick={() => handleBadgeClick('communication')}>
                    Communication <BadgeCount active={activeBadges.includes('communication')}>{badgeData.communication.length}</BadgeCount>
                  </FilterBadge>
                )}
                {badgeData.aptitude.length > 0 && (
                  <FilterBadge active={activeBadges.includes('aptitude')} onClick={() => handleBadgeClick('aptitude')}>
                    Aptitude <BadgeCount active={activeBadges.includes('aptitude')}>{badgeData.aptitude.length}</BadgeCount>
                  </FilterBadge>
                )}
                {badgeData.matchingSpec.length > 0 && badgeData.studentSpecName && (
                  <FilterBadge active={activeBadges.includes('my-spec')} onClick={() => handleBadgeClick('my-spec')}>
                    {badgeData.studentSpecName} <BadgeCount active={activeBadges.includes('my-spec')}>{badgeData.matchingSpec.length}</BadgeCount>
                  </FilterBadge>
                )}
                {visibleOtherSpecs.map(specName => (
                  <FilterBadge key={specName} active={activeBadges.includes(`spec:${specName}`)} onClick={() => handleBadgeClick(`spec:${specName}`)}>
                    {specName} <BadgeCount active={activeBadges.includes(`spec:${specName}`)}>{badgeData.otherSpecs[specName].length}</BadgeCount>
                  </FilterBadge>
                ))}
                {!showAllSpecBadges && hiddenSpecCount > 0 && (
                  <MoreBadgesButton onClick={() => setShowAllSpecBadges(true)}>+{hiddenSpecCount} more</MoreBadgesButton>
                )}
                {Object.keys(badgeData.deptScopes).map(deptName => (
                  <FilterBadge key={deptName} active={activeBadges.includes(`dept:${deptName}`)} onClick={() => handleBadgeClick(`dept:${deptName}`)}>
                    {deptName} <BadgeCount active={activeBadges.includes(`dept:${deptName}`)}>{badgeData.deptScopes[deptName].length}</BadgeCount>
                  </FilterBadge>
                ))}
              </FilterBadgeBar>
            )}

            <ContentContainer visible={true}>
              {activeTab === 'active' ? (
                <div>
                  {/* Diagnosis Sections - Only for institute tab and not corporate-only students */}
                  {!isStudentCorporateOnly && activeEntityTab === 'institute' && diagnosisData.communication.needsDiagnosis &&
                   diagnosisData.communication.assessments.length > 0 &&
                   (activeBadges.length === 0 || activeBadges.includes('communication')) && (
                    <DiagnosisSection
                      type="Communication"
                      assessments={diagnosisData.communication.assessments}
                      onTakeAssessment={handleTakeAssessment}
                    />
                  )}

                  {!isStudentCorporateOnly && activeEntityTab === 'institute' && diagnosisData.aptitude.needsDiagnosis &&
                   diagnosisData.aptitude.assessments.length > 0 &&
                   (activeBadges.length === 0 || activeBadges.includes('aptitude')) && (
                    <DiagnosisSection
                      type="Aptitude"
                      assessments={diagnosisData.aptitude.assessments}
                      onTakeAssessment={handleTakeAssessment}
                    />
                  )}

                  {/* Regular Assessments Table */}
                  <AssessmentTable
                    activeTab={activeTab}
                    activeAssessments={remainingAssessments}
                    completedAssessments={filteredCompletedAssessments}
                    dropoutAssessments={filteredDropoutAssessments}
                    handleTakeAssessment={handleTakeAssessment}
                    handleViewReport={handleViewReport}
                    activeAssessmentsLoading={activeAssessmentsLoading}
                    completedAssessmentsLoading={completedAssessmentsLoading}
                    dropoutAssessmentsLoading={dropoutAssessmentsLoading}
                    showTitle={remainingAssessments.length > 0}
                    diagnosisCompleted={diagnosisCompleted}
                    individualDiagnosisStatus={individualDiagnosisStatus}
                    hasDiagnosisSections={hasDiagnosisSections}
                    activeEntityTab={activeEntityTab}
                    isStudentCorporateOnly={isStudentCorporateOnly}
                  />
                </div>
              ) : (
                <AssessmentTable
                  activeTab={activeTab}
                  activeAssessments={[]}
                  completedAssessments={filteredCompletedAssessments}
                  dropoutAssessments={filteredDropoutAssessments}
                  handleTakeAssessment={handleTakeAssessment}
                  handleViewReport={handleViewReport}
                  activeAssessmentsLoading={activeAssessmentsLoading}
                  completedAssessmentsLoading={completedAssessmentsLoading}
                  dropoutAssessmentsLoading={dropoutAssessmentsLoading}
                  hasDiagnosisSections={false}
                  individualDiagnosisStatus={{ communication: true, aptitude: true }}
                  activeEntityTab={activeEntityTab}
                  isStudentCorporateOnly={isStudentCorporateOnly}
                />
              )}
            </ContentContainer>
          </AssessmentCard>
        </Col>
      </Row>
    </>
  )
}

export default AssessmentContainer